// generic-grid.component.ts
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { AlertMessageService } from '../../services/alert-message.service';

export interface GridColumn {
  field: string;
  header: string;
  width?: string;
  sortFieldName?: string;
  sortable?: boolean;
  headerTemplate?: TemplateRef<any> | null;
  template?: TemplateRef<any> | null;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
}

export type HeaderGroup = {
  label?: string;
  span: number;
  class?: 'closed' | 'bench' | string;
  align?: 'left' | 'center' | 'right';
};

export interface GridToolbarAction {
  id: string;
  label: string;
  iconSvg?: string;
  disabled?: boolean;
}

export interface GridAction {
  id: string;
  label: string;
  icon?: string; // emoji o clase (pi pi-*)
  visible?: boolean | ((row: any) => boolean);
  disabled?: boolean | ((row: any) => boolean);
  confirm?: string | ((row: any) => string);
}

export interface LazyLoadEventLike {
  first: number;
  rows: number;
  sortField?: string | null;
  sortOrder?: 1 | -1 | 0 | null;
  sortParam?: string | null;
  filters?: Record<string, any> | null;
  globalFilter?: string | null;
}

export type SortChange = {
  sortField: string | null;
  sortOrder: 1 | -1 | 0 | null;
  sortParam: string;
};

@Component({
  selector: 'app-generic-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownModule],
  templateUrl: './generic-grid.component.html',
  styleUrls: ['./generic-grid.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class GenericGridComponent implements OnChanges, AfterViewInit {
  constructor(private alertMessage: AlertMessageService) {}

  // ======= Inputs (API) =======
  @Input() showHeader = true;
  @Input() columns: GridColumn[] = [];
  @Input() value: any[] = [];

  @Input() lazy = false;
  @Input() totalRecords = 0;
  @Input() loading = false;

  @Input() colWidths: string[] | null = null;

  // Paginación
  @Input() paginator = true;
  @Input() rows = 20;
  @Input() rowsPerPageOptions: number[] = [10, 20, 25, 50, 100];
  @Input() showCurrentPageReport = true;
  @Input() currentPageReportTemplate = 'Page {currentPage} of {totalPages}';

  // Filtros & búsqueda
  @Input() filters: Record<string, any> = { mco: '', provider: '', dob: '' };
  @Input() globalFilter: string | null = null;

  // Interacción de filas
  @Input() rowClickable = true;
  @Input() dataKey: string | null = null;

  // Header groups opcional
  @Input() headerGroups: HeaderGroup[] | null = null;

  // ======= Outputs =======
  @Output() lazyLoad = new EventEmitter<LazyLoadEventLike>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() filtersChange = new EventEmitter<Record<string, any>>();
  @Output() sortChange = new EventEmitter<SortChange>();

  // ======= Toolbar (opcional) =======
  @Input() toolbarTitle = 'Options';
  @Input() toolbarActions: GridToolbarAction[] | null = null;
  @Output() toolbarAction = new EventEmitter<string>();

  // ======= Actions por fila (opcional) =======
  @Input() actions: GridAction[] | null = null;
  @Input() actionsHeader: string = 'Actions';
  @Input() actionsColWidth: string = '56px';
  @Output() actionSelect = new EventEmitter<{ actionId: string; row: any }>();

  // ======= Estado interno =======
  toolbarOpen = false;
  pageIndex = 0; // zero-based
  sortField: string | null = null;
  sortOrder: 1 | -1 | 0 | null = 0;
  openMenuRowKey: string | number | null = null;

  // ======= Helpers =======
  get hasHeaderGroups(): boolean {
    return (this.headerGroups?.length ?? 0) > 0;
  }

  getValue(row: any, path: string) {
    return (path || '').split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);
  }

  // ======= Ciclo de vida =======
  ngOnChanges(changes: SimpleChanges): void {
    // Asegura que `rows` esté en el selector siempre
    if ('rows' in changes || 'rowsPerPageOptions' in changes) {
      const opts = (this.rowsPerPageOptions?.length ? this.rowsPerPageOptions : [10, 20, 25, 50, 100]).slice();
      if (!opts.includes(this.rows)) {
        const set = new Set<number>([this.rows, ...opts]);
        this.rowsPerPageOptions = Array.from(set).sort((a, b) => a - b);
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.lazy) {
      Promise.resolve().then(() => this.emitLazy());
    }
  }

  /** Host listeners para toolbar y menú de acciones */
  @HostListener('document:click')
  _closeToolbar() { this.toolbarOpen = false; }

  @HostListener('document:keydown.escape')
  _escToolbar() { this.toolbarOpen = false; this.closeMenus(); }

  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    const t = ev.target as HTMLElement;
    if (t.closest('.action-cell') || t.closest('.grid-toolbar')) return;
    this.closeMenus();
    this.toolbarOpen = false;
  }

  // ======= Toolbar =======
  toggleToolbar(ev: MouseEvent) {
    ev.stopPropagation();
    this.toolbarOpen = !this.toolbarOpen;
  }

  onToolbarActionClick(a: GridToolbarAction, ev: MouseEvent) {
    ev.stopPropagation();
    if (a.disabled) return;
    this.toolbarAction.emit(a.id);
    this.toolbarOpen = false;
  }

  // ======= Menú por fila =======
  private rowKey(row: any, index?: number) {
    return this.dataKey ? (row?.[this.dataKey] ?? index ?? '') : (index ?? '');
  }

  isVisible(a: GridAction, row: any) {
    return typeof a.visible === 'function' ? !!a.visible(row) : (a.visible ?? true);
  }

  isDisabled(a: GridAction, row: any) {
    return typeof a.disabled === 'function' ? !!a.disabled(row) : (a.disabled ?? false);
  }

  toggleRowMenu(row: any, i: number, ev: MouseEvent) {
    ev.stopPropagation();
    const k = this.rowKey(row, i);
    this.openMenuRowKey = (this.openMenuRowKey === k) ? null : k;
  }

  closeMenus() { this.openMenuRowKey = null; }

  onActionClick(a: GridAction, row: any, ev: MouseEvent) {
    ev.stopPropagation();
    if (this.isDisabled(a, row)) return;

    const msg = typeof a.confirm === 'function' ? a.confirm(row) : a.confirm;

    const doEmit = () => {
      this.actionSelect.emit({ actionId: a.id, row });
      this.closeMenus();
    };

    if (msg) {
      this.alertMessage
        .showQuestion('Please confirm', msg, 'Yes', 'No')
        .then(res => { if (res.isConfirmed) doEmit(); else this.closeMenus(); });
    } else {
      doEmit();
    }
  }

  // ======= Derivados =======
  get total(): number {
    return this.lazy ? this.totalRecords : this.filteredClientData.length;
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.rows));
  }

  get pageReport(): string {
    return (this.currentPageReportTemplate || '')
      .replace('{currentPage}', String(this.pageIndex + 1))
      .replace('{totalPages}', String(this.totalPages));
  }

  /** Build sort param tipo PrimeNG: "field" | "-field" */
  get sortParam(): string {
    return this.sortField ? (((this.sortOrder || 1) === 1 ? '' : '-') + this.sortField) : '';
  }

  // ======= Pipeline cliente (si no es lazy) =======
  get filteredClientData(): any[] {
    if (this.lazy) return this.value;

    let rows = [...this.value];

    const mco = this.filters?.mco;
    const provider = this.filters?.provider;
    const dob = this.filters?.dob;

    rows = rows.filter(r => {
      const okMco = !mco || mco === 'All' || String(r?.mco ?? '') === mco;
      const okProv = !provider || provider === 'All' || String(r?.provider ?? '') === provider;
      const okDob = !dob || String(r?.dob ?? '') === dob;
      return okMco && okProv && okDob;
    });

    if (this.sortField && this.sortOrder) {
      const f = this.sortField;
      const dir = this.sortOrder;
      rows.sort((a, b) => {
        const av = this.getValue(a, f);
        const bv = this.getValue(b, f);
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }

    return rows;
  }

  get pageSlice(): any[] {
    if (this.lazy) return this.value;
    const start = this.pageIndex * this.rows;
    return this.filteredClientData.slice(start, start + this.rows);
  }

  // ======= Acciones =======
  toggleSort(col: GridColumn) {
    if (!col.sortable) return;

    const sortKey = col.sortFieldName || col.field;

    if (this.sortField !== sortKey) {
      this.sortField = sortKey;
      this.sortOrder = 1; // asc
    } else {
      this.sortOrder = this.sortOrder === 1 ? -1 : this.sortOrder === -1 ? 0 : 1;
      if (this.sortOrder === 0) this.sortField = null;
    }
    this.pageIndex = 0;

    this.sortChange.emit({
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      sortParam: this.sortParam
    });

    if (this.lazy) this.emitLazy();
  }

  changeRowsPerPage(v: number | string) {
    const n = Number(v);
    this.rows = Number.isFinite(n) ? n : this.rows;
    this.pageIndex = 0;
    if (this.lazy) this.emitLazy();
  }

  gotoFirst() { if (this.pageIndex > 0) { this.pageIndex = 0; if (this.lazy) this.emitLazy(); } }
  gotoPrev()  { if (this.pageIndex > 0) { this.pageIndex -= 1; if (this.lazy) this.emitLazy(); } }
  gotoNext()  { if (this.pageIndex < this.totalPages - 1) { this.pageIndex += 1; if (this.lazy) this.emitLazy(); } }
  gotoLast()  { if (this.pageIndex < this.totalPages - 1) { this.pageIndex = this.totalPages - 1; if (this.lazy) this.emitLazy(); } }

  applyFilters() {
    this.pageIndex = 0;
    this.filtersChange.emit({ ...(this.filters || {}) });
    if (this.lazy) this.emitLazy();
  }

  clearFilters() {
    this.filters = { mco: '', provider: '', dob: '' };
    this.applyFilters();
  }

  onRowClicked(row: any) {
    if (!this.rowClickable) return;
    this.rowClick.emit(row);
  }

  // ======= Lazy emit =======
  private emitLazy() {
    this.lazyLoad.emit({
      first: this.pageIndex * this.rows,
      rows: this.rows,
      sortField: this.sortField,
      sortOrder: (this.sortOrder || 0) as any,
      sortParam: this.sortParam || null,
      filters: { ...(this.filters || {}) },
      globalFilter: this.globalFilter || null
    });
  }

  public reload() { if (this.lazy) this.emitLazy(); }
}
